<script module lang="ts">
  export type DonutSeg = {
    label: string;
    value: number;
    color: string;
    /** Pre-formatted secondary metric shown in the legend (e.g. "×1.62" or "+4"). */
    sub?: string;
    subClass?: string;
    href?: string;
  };
</script>

<script lang="ts">
  import { navigate } from '../router.svelte';
  import ChartTooltip from '@ui/ChartTooltip.svelte';

  // The one multi-segment donut + legend used across the insights pages (posts by
  // category, contributor roles, engagement momentum). Hovering a slice or a legend
  // row highlights it and shows its share of the total.
  let {
    segments,
    center = '',
    top = 0,
    unit = '',
  }: { segments: DonutSeg[]; center?: string; top?: number; unit?: string } = $props();

  const OTHER = 'color-mix(in oklab, var(--color-base-content) 22%, transparent)';
  const SIZE = 128;
  const STROKE = 20;
  const R = (SIZE - STROKE) / 2;
  const C = SIZE / 2;
  const CIRC = 2 * Math.PI * R;

  // `top > 0` rolls the long tail into "Other" (sorted by value); otherwise the
  // caller's order/segments are kept verbatim.
  const rows = $derived.by<DonutSeg[]>(() => {
    if (top > 0) {
      const active = segments.filter((s) => s.value > 0).sort((a, b) => b.value - a.value);
      const head = active.slice(0, top);
      const tail = active.slice(top);
      const tv = tail.reduce((s, x) => s + x.value, 0);
      return tv > 0 ? [...head, { label: `Other (${tail.length})`, value: tv, color: OTHER }] : head;
    }
    return segments;
  });
  const totalVal = $derived(rows.reduce((s, x) => s + x.value, 0));
  const arcs = $derived.by(() => {
    const total = Math.max(1, totalVal);
    let acc = 0;
    return rows
      .filter((s) => s.value > 0)
      .map((s) => {
        const frac = s.value / total;
        const a = { ...s, frac, offset: acc };
        acc += frac;
        return a;
      });
  });

  let hover = $state<{ key: string; value: number; sub?: string | undefined; x: number; y: number } | null>(null);
  function enter(e: MouseEvent, s: DonutSeg): void {
    hover = { key: s.label, value: s.value, sub: s.sub, x: e.clientX, y: e.clientY };
  }
  function enterRow(e: MouseEvent, s: DonutSeg): void {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    hover = { key: s.label, value: s.value, sub: s.sub, x: r.left + r.width / 2, y: r.top };
  }
  const pct = (v: number): number => Math.round((v / Math.max(1, totalVal)) * 100);
</script>

<div class="flex items-center gap-5">
  <div class="relative inline-flex shrink-0 items-center justify-center" style="width:{SIZE}px;height:{SIZE}px">
    <svg width={SIZE} height={SIZE} viewBox="0 0 {SIZE} {SIZE}" role="img" aria-label={center}>
      <circle cx={C} cy={C} r={R} fill="none" stroke="color-mix(in oklab, var(--color-base-content) 8%, transparent)" stroke-width={STROKE} />
      {#each arcs as s (s.label)}
        <!-- svelte-ignore a11y_no_static_element_interactions -->
        <circle
          cx={C} cy={C} r={R} fill="none" stroke={s.color}
          stroke-width={hover && hover.key === s.label ? STROKE + 4 : STROKE}
          stroke-dasharray="{(s.frac * CIRC).toFixed(2)} {CIRC.toFixed(2)}"
          stroke-dashoffset={(-s.offset * CIRC).toFixed(2)}
          transform="rotate(-90 {C} {C})"
          class="cursor-pointer transition-[stroke-width,opacity]"
          opacity={hover && hover.key !== s.label ? 0.35 : 1}
          onmouseenter={(e) => enter(e, s)}
          onmousemove={(e) => hover && (hover = { ...hover, x: e.clientX, y: e.clientY })}
          onmouseleave={() => (hover = null)}
        />
      {/each}
    </svg>
    <div class="pointer-events-none absolute flex flex-col items-center leading-none">
      <span class="font-display text-lg font-bold tabular-nums">{totalVal.toLocaleString()}</span>
      {#if center}<span class="mt-0.5 font-mono text-[0.55rem] text-base-content/68">{center}</span>{/if}
    </div>
  </div>
  <ul class="min-w-0 flex-1 space-y-1.5 text-xs">
    {#each rows as s (s.label)}
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <li
        class="flex items-center gap-2 rounded transition-opacity {hover && hover.key !== s.label ? 'opacity-45' : ''}"
        onmouseenter={(e) => enterRow(e, s)}
        onmouseleave={() => (hover = null)}
      >
        <span class="h-2.5 w-2.5 shrink-0 rounded-sm" style:background={s.color}></span>
        {#if s.href}
          {@const href = s.href}
          <a class="min-w-0 flex-1 truncate transition-colors hover:text-primary hover:underline" {href} onclick={(e) => { e.preventDefault(); navigate(href); }}>{s.label}</a>
        {:else}
          <span class="min-w-0 flex-1 truncate">{s.label}</span>
        {/if}
        <span class="w-9 text-right font-mono font-medium tabular-nums">{s.value.toLocaleString()}</span>
        {#if s.sub != null}
          <span class="w-12 text-right font-mono tabular-nums text-[0.7rem] {s.subClass ?? 'text-base-content/65'}">{s.sub}</span>
        {/if}
      </li>
    {/each}
  </ul>
</div>

{#if hover}
  <ChartTooltip x={hover.x} y={hover.y}>
    <!-- The hover key is a segment NAME, not a value — left sharp on purpose. -->
    <div class="font-mono text-[0.7rem] font-semibold">{hover.key}</div>
    <div class="mt-0.5 text-xs text-base-content/70">
      <span class="font-semibold">{hover.value.toLocaleString()}</span>{unit ? ` ${unit}` : ''}
      <span class="text-base-content/65"> · {pct(hover.value)}% of {totalVal.toLocaleString()}</span>
    </div>
    {#if hover.sub}<div class="mt-0.5 font-mono text-[0.62rem] text-base-content/68">{hover.sub}</div>{/if}
  </ChartTooltip>
{/if}
